import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface ThumbOnlyVolumeSliderProps {
  volume: number;
  disabled?: boolean;
  trackInsetX?: number;
  onVolumeChange: (volume: number) => void;
  onVolumeCommit: (volume: number) => void;
}

const THUMB_SIZE = 14;
const THUMB_HIT = 28;

const ThumbOnlyVolumeSlider: React.FC<ThumbOnlyVolumeSliderProps> = ({
  volume,
  disabled,
  trackInsetX = 0,
  onVolumeChange,
  onVolumeCommit,
}) => {
  const [localVol, setLocalVol] = useState(volume ?? 0);
  const draggingRef = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const localVolRef = useRef(localVol);

  useEffect(() => { localVolRef.current = localVol; }, [localVol]);

  useEffect(() => {
    if (!draggingRef.current) setLocalVol(volume ?? 0);
  }, [volume]);

  const posFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return localVolRef.current;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return localVolRef.current;
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  }, []);

  const finishDrag = useCallback((clientX: number) => {
    draggingRef.current = false;
    const v = posFromClientX(clientX);
    setLocalVol(v);
    onVolumeChange(v);
    onVolumeCommit(v);
  }, [onVolumeChange, onVolumeCommit, posFromClientX]);

  const handleThumbPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleThumbPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const v = posFromClientX(e.clientX);
    setLocalVol(v);
    onVolumeChange(v);
  };

  const handleThumbPointerUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    finishDrag(e.clientX);
  };

  return (
    <Box
      data-audio-no-swipe=""
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        ref={trackRef}
        sx={{
          flex: 1,
          height: THUMB_HIT,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          touchAction: 'pan-y',
          mx: trackInsetX,
        }}
      >
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: 3,
          borderRadius: 2,
          backgroundColor: 'rgba(255,255,255,0.25)',
        }}>
          <Box sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${localVol}%`,
            borderRadius: 2,
            backgroundColor: '#fff',
          }}
          />
        </Box>
        <Box
          onPointerDown={handleThumbPointerDown}
          onPointerMove={handleThumbPointerMove}
          onPointerUp={handleThumbPointerUp}
          onPointerCancel={handleThumbPointerUp}
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${localVol}%`,
            width: THUMB_HIT,
            height: THUMB_HIT,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            cursor: disabled ? 'default' : 'grab',
          }}
        >
          <Box sx={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            borderRadius: '50%',
            backgroundColor: '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
          }}
          />
        </Box>
      </Box>
      <Typography sx={{
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        minWidth: 22,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}>
        {localVol}
      </Typography>
    </Box>
  );
};

export default ThumbOnlyVolumeSlider;
